import PhotoCameraOutlined from "@mui/icons-material/PhotoCameraOutlined"
import { Box, Button, IconButton, Tooltip, alpha } from "@mui/material"
import { useFeatureFlagPayload } from "posthog-js/react"
import { useCallback, useId, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link as RouterLink, useLocation } from "react-router"

import { pxToRem } from "@sap/theme/typography"

import { useAuthStore } from "@/store/auth"
import { useImageSearchAnnouncementStore } from "@/store/imageSearchAnnouncement"

import { IMAGE_SEARCH_FEATURE_FLAG, useImageSearchAccess } from "./access"
import { sendImageSearchEvent } from "./analytics"
import ImageSearchAnnouncement from "./ImageSearchAnnouncement"

export default function ImageSearchButton({
  onClick,
  variant = "icon",
  announcement = true,
}: {
  onClick?: () => void
  variant?: "icon" | "pill"
  announcement?: boolean
}) {
  const enabled = useImageSearchAccess()
  const payload = useFeatureFlagPayload(IMAGE_SEARCH_FEATURE_FLAG)
  const userId = useAuthStore((state) => state.user?.id)
  const dismissed = useImageSearchAnnouncementStore(
    (state) => userId != null && state.dismissedByUser[userId] === true
  )
  const dismiss = useImageSearchAnnouncementStore((state) => state.dismiss)
  const [anchorEl, setAnchorEl] = useState<HTMLAnchorElement | null>(null)
  const announcementId = useId()
  const { pathname } = useLocation()
  const { t } = useTranslation("common", { keyPrefix: "imageSearch" })
  const showAnnouncement =
    announcement &&
    userId != null &&
    !dismissed &&
    pathname !== "/image-search" &&
    payload !== null &&
    typeof payload === "object" &&
    "showAnnouncement" in payload &&
    payload.showAnnouncement === true
  const dismissAnnouncement = useCallback(() => {
    if (userId != null) dismiss(userId)
  }, [dismiss, userId])
  const handleClick = () => {
    sendImageSearchEvent("image_search_opened", { variant })
    dismissAnnouncement()
    onClick?.()
  }
  if (!enabled) return null
  return (
    <>
      <Tooltip title={showAnnouncement ? "" : t("title")} arrow>
        {variant === "pill" ? (
          <Button
            component={RouterLink}
            to="/image-search"
            aria-label={t("title")}
            aria-describedby={showAnnouncement ? `${announcementId}-description` : undefined}
            ref={setAnchorEl}
            onClick={handleClick}
            variant="outlined"
            color="inherit"
            sx={{
              minWidth: 36,
              height: 36,
              flexShrink: 0,
              gap: { xs: 0, sm: 0.75 },
              px: { xs: 0, sm: 1.25 },
              py: 0,
              borderRadius: 300,
              borderColor: "grey.300",
              backgroundColor: "action.hover",
              boxShadow: (theme) => `0 2px 6px ${alpha(theme.palette.grey[900], 0.06)}`,
              color: "text.primary",
              fontSize: pxToRem(14),
              fontWeight: 500,
              textTransform: "none",
              "&:hover": {
                borderColor: "grey.400",
                backgroundColor: "action.selected",
              },
              "&.Mui-focusVisible": {
                outline: "2px solid",
                outlineColor: "primary.main",
                outlineOffset: -3,
              },
            }}
          >
            <PhotoCameraOutlined sx={{ fontSize: pxToRem(20) }} />
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              {t("photo")}
            </Box>
          </Button>
        ) : (
          <IconButton
            component={RouterLink}
            to="/image-search"
            aria-label={t("title")}
            aria-describedby={showAnnouncement ? `${announcementId}-description` : undefined}
            ref={setAnchorEl}
            onClick={handleClick}
          >
            <PhotoCameraOutlined sx={{ fontSize: pxToRem(20) }} />
          </IconButton>
        )}
      </Tooltip>
      {showAnnouncement && anchorEl && (
        <ImageSearchAnnouncement
          id={announcementId}
          anchorEl={anchorEl}
          onDismiss={dismissAnnouncement}
        />
      )}
    </>
  )
}
